/**
 * Presence bridge — connects SignalR PresenceChanged events to the presence store.
 *
 * Listens for real-time presence updates (user online/offline) and maintains
 * the client-side presence map for UI consumption.
 *
 * Also handles PresenceBulkSync — sent by the server on initial connection
 * with the full list of currently online users, so refreshing a page
 * doesn't lose knowledge of who's already online.
 */
import { onChatEvent, ChatHubEvents } from '../api/chatConnection';
import { updatePresence, clearPresence } from './presenceStore';

let cleanupFns: Array<() => void> = [];

/**
 * Normalize server status strings to the client-side PresenceStatus type.
 * Server sends "away" for idle users; client store uses "idle".
 */
function normalizeStatus(raw: string): 'online' | 'idle' | 'offline' {
  if (raw === 'online') return 'online';
  if (raw === 'away' || raw === 'idle') return 'idle';
  return 'offline';
}

/**
 * Start listening for presence changes.
 * Call once on app mount. Returns a cleanup function.
 */
export function startPresenceBridge(): () => void {
  // Listen for individual PresenceChanged events from SignalR
  const unsubPresence = onChatEvent(
    ChatHubEvents.PresenceChanged,
    (data: any) => {
      if (data?.userGuid && data?.status) {
        const mapped = normalizeStatus(data.status);
        updatePresence({
          userGuid: data.userGuid,
          status: mapped,
          connectedAt: data.connectedAt,
          disconnectedAt: data.disconnectedAt,
        });
      }
    }
  );
  cleanupFns.push(unsubPresence);

  // Listen for bulk presence sync (sent on initial connection)
  const unsubBulkSync = onChatEvent(
    ChatHubEvents.PresenceBulkSync,
    (data: any) => {
      if (Array.isArray(data)) {
        data.forEach((entry: any) => {
          if (entry?.userGuid && entry?.status) {
            updatePresence({
              userGuid: entry.userGuid,
              status: normalizeStatus(entry.status),
            });
          }
        });
        console.debug('[PresenceBridge] Bulk sync complete:', data.length, 'users');
      }
    }
  );
  cleanupFns.push(unsubBulkSync);

  // Return cleanup
  return () => {
    cleanupFns.forEach((fn) => fn());
    cleanupFns = [];
    clearPresence();
  };
}
